import React from 'react';
import type {SharedOptions} from '@packages/systems/feature-config/shared';
import {FeatureConfigContext} from '../context';
import {notifyObservers} from '../observers';
import {overrideAdapter} from '../overrideAdapter';
import {getStatsigClient, statsigClientExists} from '../client';

export type ConfigShape = Record<string, unknown>;

const defaultConfigOptions = {shouldLogExposure: true};
export function getConfigOutsideOfReact<ConfigObject extends ConfigShape>(
  config: string,
  defaultConfig?: undefined,
  configOptions?: SharedOptions
): Partial<ConfigObject>;
export function getConfigOutsideOfReact<ConfigObject extends ConfigShape>(
  config: string,
  defaultConfig: ConfigObject,
  configOptions?: SharedOptions
): ConfigObject;
export function getConfigOutsideOfReact<ConfigObject extends ConfigShape>(
  config: string,
  defaultConfig?: ConfigObject,
  configOptions: SharedOptions = defaultConfigOptions
) {
  if (statsigClientExists()) {
    const statsigConfig = getStatsigClient().getDynamicConfig(
      config.toLowerCase(),
      {
        disableExposureLog: !configOptions.shouldLogExposure,
      }
    );

    return {
      ...defaultConfig,
      ...statsigConfig.value,
    };
  }

  if (defaultConfig) {
    return defaultConfig;
  }

  return {};
}

export const setConfig = <ConfigObject extends ConfigShape>(
  config: string,
  value: Partial<ConfigObject>
) => {
  if (statsigClientExists()) {
    overrideAdapter.overrideDynamicConfig(config.toLowerCase(), value);
    notifyObservers();
  }
};

export const resetConfig = (config: string) => {
  if (statsigClientExists()) {
    overrideAdapter.removeDynamicConfigOverride(config.toLowerCase());
    notifyObservers();
  }
};

export function useConfig<ConfigObject extends ConfigShape>(
  config: string,
  defaultConfig?: undefined,
  configOptions?: SharedOptions
): Partial<ConfigObject>;
export function useConfig<ConfigObject extends ConfigShape>(
  config: string,
  defaultConfig: ConfigObject,
  configOptions?: SharedOptions
): ConfigObject;
export function useConfig<ConfigObject extends ConfigShape>(
  config: string,
  defaultConfig?: ConfigObject,
  configOptions: SharedOptions = defaultConfigOptions
) {
  // using context as a way to force updates
  const _config = React.useContext(FeatureConfigContext);

  return defaultConfig
    ? getConfigOutsideOfReact(config, defaultConfig, configOptions)
    : getConfigOutsideOfReact(config, undefined, configOptions);
}
